"use client";

/**
 * A single tab button inside `<ProductQuickNav />`.
 *
 * Kept as its own component so the parent rail can use refs + a stable
 * array of DOM nodes for arrow-key navigation without passing a ref
 * factory through JSX.
 *
 * Glyph variants:
 *   1. `displayColour === "multi"` → conic-gradient rainbow swatch.
 *   2. `displayColour` set to any other value → solid swatch of that hex
 *      (colour-filter tabs ARE their colour).
 *   3. otherwise → a circle filled with the tab's SEMANTIC accent gradient
 *      (fresh→green, crochet→violet, best-sellers→gold, …) + a white icon, so
 *      each category reads as its own thing rather than every tab sharing one
 *      sage circle. See `getNavTabAccent`.
 *
 * Selected state: a clearly-distinct treatment — a filled `bg-muted` pill, a
 * sage "you are here" bar on the left edge, a bold dark label, and a sage ring
 * around the glyph — so it's obvious which tab is active (the previous
 * sage-fill-on-sage-icon blended together).
 *
 * ── WHY THIS IS AN <a>, NOT A <button> (QNAVLINK) ────────────────────────────
 * Every tab's behaviour IS navigation: it puts `?tab=<slug>` on the URL (and the
 * synthetic "All" tab — plus tapping the active tab again — removes it). Rendered
 * as a `<button>` the rail shipped ZERO crawlable hrefs on `/products`, a primary
 * commercial route, so the eight-or-so quick-filter views were reachable only by
 * running JavaScript. The href is fully derivable from the current query string,
 * so there is no behaviour that needed a button.
 *
 * `role="tab"` + `aria-selected` + the roving tabindex are preserved (ARIA
 * permits the role on an anchor), and every class is byte-identical — only the
 * tag and the activation mechanism changed. `prefetch={false}` is deliberate:
 * `/products` is `force-dynamic`, so prefetching ~8 sibling URLs would mean ~8
 * extra dynamic server renders per phone visit.
 */

import Link from "next/link";
import { getNavIcon, getNavTabAccent, NAV_ICON_STROKE } from "@/lib/nav-tab-icons";
import { cn } from "@/lib/utils";

export interface ProductQuickNavTabProps {
  slug: string;
  label: string;
  iconName: string;
  active: boolean;
  /**
   * The URL this tab navigates to — a real, crawlable href built from the
   * current query string by `ProductQuickNav.hrefForSlug`.
   */
  href: string;
  /** Tab index position — used for `id` so `aria-controls` resolves. */
  index: number;
  /**
   * Hex colour for colour-filter tabs — when set, the glyph is a filled
   * swatch of that colour rather than the stroke icon.
   */
  displayColour?: string;
  onKeyDown: (e: React.KeyboardEvent<HTMLAnchorElement>) => void;
  /**
   * REACT19-1 — `ref` is a REGULAR PROP in React 19; no `forwardRef` wrapper.
   * Typed `Ref<…>` (not `RefObject<…>`) because `ProductQuickNav` passes a
   * CALLBACK ref to collect the rail's DOM nodes for arrow-key navigation.
   */
  ref?: React.Ref<HTMLAnchorElement>;
}

export function ProductQuickNavTab({
  slug,
  label,
  iconName,
  active,
  href,
  index,
  displayColour,
  onKeyDown,
  ref,
}: ProductQuickNavTabProps) {
  const Icon = getNavIcon(iconName);
  const accent = getNavTabAccent(`${slug} ${label}`);
  return (
    <Link
      ref={ref}
      href={href}
      // `replace` + `scroll={false}` reproduce exactly what the old onClick did
      // (`router.replace(target, { scroll: false })`): toggling a quick filter is
      // not a new history entry, and it must not jump the grid back to the top.
      replace
      scroll={false}
      prefetch={false}
      role="tab"
      id={`product-nav-tab-${slug}`}
      aria-selected={active}
      // Roving tabindex: only the active (or first, when none active) tab
      // is reachable by Tab. Arrow keys move within the rail.
      tabIndex={active ? 0 : -1}
      data-index={index}
      data-slug={slug}
      data-active={active ? "true" : "false"}
      onKeyDown={onKeyDown}
      title={label}
      className={cn(
        // Vertical tab. Glyph + label sizes scale up at min-[400px] so
        // the same component fits both the 80px rail (smallest phones)
        // and the 88px rail (≥400px). Labels wrap whole-word only (no
        // line-clamp, no mid-word break) so nothing is hidden or split.
        //
        // The selected indicator is drawn OUTSIDE the button via a left
        // box-shadow (`shadow-[-3px_0_0_0_var(--sage)]`) — it reserves NO inner
        // width and can't overlap the label, unlike a border (which shrank the
        // content) or the earlier absolute bar (which sat on top of the text).
        // Padding is kept minimal (px-0.5) so the narrow rail gives the label
        // as much width as possible.
        "group relative flex w-full min-h-11 flex-col items-center justify-center gap-1 rounded-lg px-0.5 py-1.5 min-[400px]:py-2 text-[10px] min-[400px]:text-[11px] font-medium leading-tight transition-all",
        "focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        active
          ? // Active = filled pill + bold dark label + a sage accent drawn
            // OUTSIDE the left edge. Deliberately NO ring/border around the
            // icon glyph — the left accent is the only edge marker.
            "bg-muted font-semibold text-foreground shadow-[-3px_0_0_0_var(--sage)]"
          : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
      )}
    >
      {displayColour === "multi" ? (
        // Multi-colour: conic gradient through the rainbow so the rail
        // shows what "multi" actually means at a glance.
        <span
          aria-hidden="true"
          className="size-6 min-[400px]:size-7 shrink-0 rounded-full border-2 border-foreground/15"
          style={{
            background:
              "conic-gradient(from 0deg, #ef4444, #f59e0b, #facc15, #22c55e, #06b6d4, #6366f1, #a855f7, #ec4899, #ef4444)",
          }}
        />
      ) : displayColour ? (
        // Filled colour swatch — border keeps pale colours (White, Cream)
        // visible against the pale page background.
        <span
          aria-hidden="true"
          className="size-6 min-[400px]:size-7 shrink-0 rounded-full border-2 border-foreground/15"
          style={{ backgroundColor: displayColour }}
        />
      ) : (
        // Circle filled with the tab's SEMANTIC accent gradient + white icon.
        <span
          aria-hidden="true"
          className={cn(
            "flex size-6 min-[400px]:size-7 shrink-0 items-center justify-center rounded-full bg-linear-to-br shadow-sm",
            accent
          )}
        >
          {/* eslint-disable-next-line react-hooks/static-components -- The renderer is selected from a stable module-level icon mapping and owns no component state. */}
          <Icon
            className="size-3.5 min-[400px]:size-4 text-white drop-shadow-sm"
            strokeWidth={NAV_ICON_STROKE}
            aria-hidden="true"
          />
        </span>
      )}
      {/* Whole-word wrapping: `break-normal` keeps each word intact and wraps
          only at spaces — so single words ("Accessories") sit on one line and
          multi-word labels ("Creative Gifts") split per word ("Creative" /
          "Gifts"), never mid-word. The rail width below is sized so the longest
          single word fits. `text-balance` evens out the wrapped lines. */}
      <span className="w-full text-center text-balance break-normal">
        {label}
      </span>
    </Link>
  );
}
